import { z } from "zod";

/**
 * Synthetic usage (spec `synthetic-usage`, openspec change `demo-world-v2`): a tenant admin of a
 * dev-tier stage asks ESI to generate a deterministic usage history for their own tenant from
 * bounded parameters, never from figures. The rows are stored marked `synthetic`, and their spend
 * is display only.
 */

/** Every metric the usage rollup writes: the synthetic generator must cover each of them. */
export const ROLLUP_METRICS = [
  "questions",
  "conversations",
  "answers",
  "answers_no_source",
  "answers_web",
  "citations",
  "web_citations",
  "ocr_flagged",
  "ingestion_failed",
  "documents_pending",
  "storage_bytes",
  "flow_denied",
  "not_found",
  "download_denied",
  "break_glass",
  "declassification_created",
  "declassification_withdrawn",
  "input_tokens",
  "output_tokens",
  "cache_read_tokens",
  "cache_write_tokens",
  "cost_usd",
  "model_calls",
  "unknown_cost_calls",
  "model_failures",
  "model_fallbacks",
  "origin_cost_usd",
  "origin_model_calls",
  "tool_calls",
  "tool_cost_usd",
  "tool_unknown_cost_calls",
  "tool_blocked",
  "tool_declined",
  "active_users",
] as const;
export type RollupMetric = (typeof ROLLUP_METRICS)[number];

/** Where a rollup figure comes from (`source` of the rollup tables). */
export const ROLLUP_SOURCES = ["live", "synthetic"] as const;
export type RollupSource = (typeof ROLLUP_SOURCES)[number];

export const SYNTHETIC_USAGE_MIN_DAYS = 30;
export const SYNTHETIC_USAGE_MAX_DAYS = 90;
/** The largest generator seed (2^31 − 1). */
export const SYNTHETIC_USAGE_MAX_SEED = 2 ** 31 - 1;
/** Synthetic spend never takes the displayed month above this share of the spend limit. */
export const SYNTHETIC_USAGE_MAX_SPEND_SHARE = 0.95;
/** At most this many entities and projects together are generated for. */
export const SYNTHETIC_USAGE_MAX_SCOPES = 50;
/** At most this many rows are written by one request. */
export const SYNTHETIC_USAGE_MAX_ROWS = 60_000;

export const SYNTHETIC_USAGE_VOLUMES = ["small", "medium", "large"] as const;
export type SyntheticUsageVolume = (typeof SYNTHETIC_USAGE_VOLUMES)[number];

/** `PUT /api/admin/synthetic-usage`: the generator's parameters, never figures. */
export const syntheticUsageRequestSchema = z
  .object({
    /** Days of history, ending today (UTC). */
    days: z.int().min(SYNTHETIC_USAGE_MIN_DAYS).max(SYNTHETIC_USAGE_MAX_DAYS),
    seed: z.int().min(0).max(SYNTHETIC_USAGE_MAX_SEED),
    volume: z.enum(SYNTHETIC_USAGE_VOLUMES),
    /**
     * Target share of this month's spend limit that live plus synthetic spend reaches (no effect
     * past what live spend already used; a volume-based figure when the limit is unlimited).
     */
    monthSpendShare: z.number().min(0).max(SYNTHETIC_USAGE_MAX_SPEND_SHARE),
  })
  .strict();
export type SyntheticUsageRequest = z.infer<typeof syntheticUsageRequestSchema>;

/** The tenant's last synthetic usage write: its parameters, row count and time. */
export const syntheticUsageStatusSchema = z
  .object({
    params: syntheticUsageRequestSchema,
    rows: z.int().min(0),
    writtenAt: z.iso.datetime(),
  })
  .strict();
export type SyntheticUsageStatus = z.infer<typeof syntheticUsageStatusSchema>;

/** `GET /api/admin/synthetic-usage`, and the `PUT` response: null before any write. */
export const syntheticUsageResponseSchema = syntheticUsageStatusSchema.nullable();
export type SyntheticUsageResponse = z.infer<typeof syntheticUsageResponseSchema>;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The UTC day `n` days after `day` (`YYYY-MM-DD`; `n` may be negative). */
export function addDays(day: string, n: number): string {
  return new Date(new Date(`${day}T00:00:00Z`).getTime() + n * DAY_MS).toISOString().slice(0, 10);
}

/** The ISO week (its Monday) of a UTC day `YYYY-MM-DD`, as the rollup stores week figures. */
export function isoWeekStart(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  return new Date(date.getTime() - ((date.getUTCDay() + 6) % 7) * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

/** The calendar month's first day of a UTC day, as the rollup stores month figures. */
export function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}
